import { DashboardLayout } from "../../client/components/Sidebar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  MapPin,
  Building2,
  Calendar,
  Megaphone,
  ShoppingBag,
  Utensils,
  Wrench,
  Briefcase,
  ShoppingCart,
  HandHeart,
  Home,
  Search,
  Car,
  Gift,
  MessageCircleQuestion,
  AlertCircle,
  HelpCircle,
  GraduationCap,
  BookOpen,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { pageLayoutStyles } from "../../components/ui/page-layout";
import type { Location } from "wasp/entities";
import { useViewMode } from "../../client/contexts/ViewModeContext";

interface DefaultReceptionPageProps {
  locations: Location[];
}

export default function DefaultReceptionPage({
  locations,
}: DefaultReceptionPageProps) {
  const navigate = useNavigate();
  const { isHome, isStudentLife, isAcademics, isCampus, isMarketplace } = useViewMode();

  const handleLocationSelect = (location: Location) => {
    if (location.locationCode) {
      navigate(`/locations/${location.locationCode}/dashboard`);
    }
  };

  const getLocationTypeIcon = (type: string) => {
    switch (type) {
      case "APARTMENT_BUILDING":
        return <Building2 className="w-5 h-5" />;
      case "INNOVATION_HUB":
        return <Building2 className="w-5 h-5" />;
      case "COWORKING_SPACE":
        return <Building2 className="w-5 h-5" />;
      case "SHOPPING_CENTER":
        return <Building2 className="w-5 h-5" />;
      case "CAMPUS":
        return <Building2 className="w-5 h-5" />;
      case "COMMUNITY_CENTER":
        return <Building2 className="w-5 h-5" />;
      default:
        return <MapPin className="w-5 h-5" />;
    }
  };

  const getLocationTypeLabel = (type: string) => {
    return type
      ?.replace(/_/g, " ")
      .toLowerCase()
      ?.replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <DashboardLayout>
      <div className={pageLayoutStyles.layout.container}>
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-foreground mb-4">
            Welcome to{" "}
            <span className="text-primary">Ottawa University</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Your community platform for staying connected with your location's
            events, notices, and services.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Home View */}
          {isHome && (
            <>
              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-purple-600"
                onClick={() => navigate("/notices")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Megaphone className="w-6 h-6 text-purple-600" />
                    <CardTitle className="text-lg">Notices</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    View community announcements and updates
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-blue-500"
                onClick={() => navigate("/events")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-6 h-6 text-blue-500" />
                    <CardTitle className="text-lg">Events</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Discover upcoming activities and events
                  </p>
                </CardContent>
              </Card>
            </>
          )}

          {/* Campus Life View */}
          {isCampus && (
            <>
              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-cyan-600"
                onClick={() => navigate("/tenants")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-6 h-6 text-cyan-600" />
                    <CardTitle className="text-lg">Tenants</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Community members
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-orange-600"
                onClick={() => navigate("/food-court")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Utensils className="w-6 h-6 text-orange-600" />
                    <CardTitle className="text-lg">Food Court</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Dining & restaurants
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-teal-600"
                onClick={() => navigate("/facilities")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-6 h-6 text-teal-600" />
                    <CardTitle className="text-lg">Facilities</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Amenities & services
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-purple-500"
                onClick={() => navigate("/services")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Wrench className="w-6 h-6 text-purple-500" />
                    <CardTitle className="text-lg">Services</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Professional services
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-emerald-500"
                onClick={() => navigate("/jobs")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-6 h-6 text-emerald-500" />
                    <CardTitle className="text-lg">Jobs</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Work opportunities
                  </p>
                </CardContent>
              </Card>
            </>
          )}

          {/* Student View */}
          {isStudentLife && (
            <>
              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-green-600"
                onClick={() => navigate("/buy-sell")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="w-6 h-6 text-green-600" />
                    <CardTitle className="text-lg">Buy & Sell</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Marketplace items
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-indigo-600"
                onClick={() => navigate("/request-offer")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <HandHeart className="w-6 h-6 text-indigo-600" />
                    <CardTitle className="text-lg">Request & Offer</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Help & collaboration
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-cyan-600"
                onClick={() => navigate("/accommodation")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Home className="w-6 h-6 text-cyan-600" />
                    <CardTitle className="text-lg">Accommodation</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Housing & rentals
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-blue-500"
                onClick={() => navigate("/lost-found")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Search className="w-6 h-6 text-blue-500" />
                    <CardTitle className="text-lg">Lost & Found</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Recover missing items
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-blue-500"
                onClick={() => navigate("/carpooling")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Car className="w-6 h-6 text-blue-500" />
                    <CardTitle className="text-lg">Car Pooling</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Shared rides
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-green-600"
                onClick={() => navigate("/share-with-neighbors")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Gift className="w-6 h-6 text-green-600" />
                    <CardTitle className="text-lg">Share with Neighbors</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Borrow & borrow
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-amber-600"
                onClick={() => navigate("/other-requests")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <MessageCircleQuestion className="w-6 h-6 text-amber-600" />
                    <CardTitle className="text-lg">Other Requests</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Miscellaneous needs
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-red-600"
                onClick={() => navigate("/immediate-help")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                    <CardTitle className="text-lg">Time Sensitive Help</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Urgent assistance
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-blue-600"
                onClick={() => navigate("/ask-for-help")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <HelpCircle className="w-6 h-6 text-blue-600" />
                    <CardTitle className="text-lg">Ask for Help</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    General support
                  </p>
                </CardContent>
              </Card>
            </>
          )}

          {/* Academics View */}
          {isAcademics && (
            <>
              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-purple-600"
                onClick={() => navigate("/services?category=TUTORING")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <GraduationCap className="w-6 h-6 text-purple-600" />
                    <CardTitle className="text-lg">Tutor Help</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Academic assistance
                  </p>
                </CardContent>
              </Card>

              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-blue-600"
                onClick={() => navigate("/textbooks")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                    <CardTitle className="text-lg">Textbooks</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Buy & sell books
                  </p>
                </CardContent>
              </Card>
            </>
          )}

          {/* Marketplace View */}
          {isMarketplace && (
            <>
              <Card
                className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-pink-600"
                onClick={() => navigate("/retail")}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <ShoppingBag className="w-6 h-6 text-pink-600" />
                    <CardTitle className="text-lg">Businesses</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    External services & stores
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Available Locations */}
        {locations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Available Locations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {locations.map((location) => (
                  <Card
                    key={location.id}
                    className="hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-primary/20"
                    onClick={() => handleLocationSelect(location)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 p-2 bg-primary/10 rounded-lg">
                          {getLocationTypeIcon(location.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground truncate">
                            {location.name}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            {getLocationTypeLabel(location.type)}
                          </p>
                          {location.city && location.state && (
                            <p className="text-xs text-muted-foreground">
                              {location.city}, {location.state}
                            </p>
                          )}
                          {location.publicDescription && (
                            <p className="text-xs text-muted-foreground mt-2 line-clamp-3 overflow-hidden">
                              {location.publicDescription}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Locations Available */}
        {locations.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No Locations Available
              </h3>
              <p className="text-muted-foreground">
                There are currently no locations available. Please contact an
                administrator.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
